// F3 calorie cycling (premium): redistribute the week without changing its
// total. Presets do the maths; the custom row keeps the weekly average pinned
// at 1.0 so cycling can never quietly become a bigger deficit. The server
// clamps every day at the safety floor regardless of what's set here.
import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { hapticSelect, hapticSuccess } from "../lib/haptics";
import { SheetShell } from "./SheetShell";
import { Paywall } from "./Paywall";
import { Button } from "./ui/Button";
import type { Cycling } from "@shared/types";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
type Day = (typeof DAYS)[number];
// Locale files key weekdays numerically with Sunday = 0.
const DAY_KEY: Record<Day, number> = { mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6, sun: 0 };

const PRESETS: Record<string, Record<Day, number>> = {
  flat: { mon: 1, tue: 1, wed: 1, thu: 1, fri: 1, sat: 1, sun: 1 },
  weekend_flex: { mon: 0.92, tue: 0.92, wed: 0.92, thu: 0.92, fri: 0.92, sat: 1.2, sun: 1.2 },
  training_rest: { mon: 1.1, tue: 0.9, wed: 1.1, thu: 0.9, fri: 1.1, sat: 0.95, sun: 0.95 },
};

interface Props {
  baselineKcal: number;
  onSaved: () => void;
  onClose: () => void;
}

export function CyclingSheet({ baselineKcal, onSaved, onClose }: Props) {
  const t = useT();
  const toast = useToast();
  const [cfg, setCfg] = useState<Cycling | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);

  useEffect(() => {
    api.cycling().then(setCfg).catch(() => setCfg(null));
  }, []);

  function applyPreset(name: keyof typeof PRESETS) {
    hapticSelect();
    setCfg((c) => (c ? { ...c, ...PRESETS[name], preset: name, enabled: name === "flat" ? 0 : 1 } : c));
  }

  async function save() {
    if (!cfg) return;
    setBusy(true);
    try {
      await api.setCycling(cfg);
      hapticSuccess();
      toast.success(t("toast.saved"));
      onSaved();
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.message === "premium_required") setShowPaywall(true);
      else if (e instanceof ApiError && e.message === "weekly_average_must_be_1") toast.error(t("cycling.avg_error"));
      else toast.error(t("common.failed"));
    } finally {
      setBusy(false);
    }
  }

  if (!cfg) return null;
  const avg = DAYS.reduce((a, d) => a + cfg[d], 0) / 7;
  const balanced = Math.abs(avg - 1) <= 0.02;

  return (
    <SheetShell onClose={onClose}>
      <h3 className="text-lg font-semibold mt-4 mb-1">{t("cycling.title")}</h3>
      <p className="text-sm text-fg-muted mb-4">{t("cycling.explainer")}</p>

      <div className="flex gap-1.5 mb-4">
        {(["flat", "weekend_flex", "training_rest"] as const).map((p) => (
          <button
            key={p}
            onClick={() => applyPreset(p)}
            aria-pressed={cfg.preset === p}
            className={`flex-1 min-h-[44px] rounded-xl text-sm font-medium transition-colors ${
              cfg.preset === p ? "bg-brand text-on-brand" : "bg-surface text-fg-secondary active:bg-surface-strong"
            }`}
          >
            {t(`cycling.preset_${p}`)}
          </button>
        ))}
      </div>

      <div className="space-y-1.5 mb-4">
        {DAYS.map((d) => (
          <div key={d} className="flex items-center gap-3">
            <span className="w-10 text-sm text-fg-muted">{t(`day.short.${DAY_KEY[d]}`)}</span>
            <input
              type="range" min={0.7} max={1.3} step={0.01}
              value={cfg[d]}
              onChange={(e) => setCfg({ ...cfg, [d]: Number(e.target.value), preset: "custom" })}
              className="flex-1 accent-white"
              aria-label={t(`day.short.${DAY_KEY[d]}`)}
            />
            <span className="w-16 text-right text-sm tabular-nums">
              {Math.round(baselineKcal * cfg[d])}
            </span>
          </div>
        ))}
      </div>

      <p className={`text-sm mb-4 ${balanced ? "text-fg-muted" : "text-warn"}`}>
        {balanced
          ? t("cycling.balanced", { kcal: String(Math.round(baselineKcal * 7)) })
          : t("cycling.avg_error")}
      </p>

      <label className="flex items-center gap-2 text-sm mb-4">
        <input
          type="checkbox"
          checked={cfg.enabled === 1}
          onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked ? 1 : 0 })}
          className="h-5 w-5 accent-white"
        />
        <span>{t("cycling.enable")}</span>
      </label>

      <Button fullWidth onClick={save} disabled={busy || (cfg.enabled === 1 && !balanced)}>
        {busy ? t("common.loading") : t("edit_sheet.save")}
      </Button>
      <div className="pb-2" />
      {showPaywall && <Paywall trigger="feature" onClose={() => setShowPaywall(false)} />}
    </SheetShell>
  );
}
